import { createContext, useContext, useState, useEffect, type ReactNode } from "react";
import { translations, type Language } from "./translations";

type LanguageContextType = {
  lang: Language;
  setLang: (lang: Language) => void;
  t: (key: string) => string;
};

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

const detectLanguage = (): Language => {
  const stored = localStorage.getItem("ck-lang") as Language | null;
  if (stored && translations[stored]) return stored;

  const browserLang = navigator.language.split("-")[0] as Language;
  if (translations[browserLang]) return browserLang;

  return "es";
};

export const LanguageProvider = ({ children }: { children: ReactNode }) => {
  const [lang, setLangState] = useState<Language>(detectLanguage);

  const setLang = (l: Language) => {
    setLangState(l);
    localStorage.setItem("ck-lang", l);
    document.documentElement.lang = l;
  };

  useEffect(() => {
    document.documentElement.lang = lang;
  }, []);

  const t = (key: string): string => {
    return (translations[lang] as Record<string, string>)[key] ?? (translations.es as Record<string, string>)[key] ?? key;
  };

  return (
    <LanguageContext.Provider value={{ lang, setLang, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

const fallback: LanguageContextType = {
  lang: "es",
  setLang: () => {},
  t: (key: string) => (translations.es as Record<string, string>)[key] ?? key,
};

export const useLanguage = (): LanguageContextType => {
  const ctx = useContext(LanguageContext);
  return ctx ?? fallback;
};
